import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setPageSeo, resetPageSeo, SEO_BASE } from "@/lib/seo";
import { Loader2, Flame, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Blog() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/blog?lang=${lang}&limit=30`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setPageSeo({
      title: "Blog du souvenir — Hommages & mémoriaux | BougieDuCœur",
      description: "Découvrez les hommages et récits du souvenir publiés sur BougieDuCœur, le premier mémorial numérique. Allumez une bougie en mémoire d'un être cher.",
      canonicalPath: "/blog",
      image: `${SEO_BASE}/og-image.png`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Blog du souvenir — BougieDuCœur",
        "url": `${SEO_BASE}/blog`,
        "inLanguage": lang,
        "publisher": { "@type": "Organization", "name": "BougieDuCœur", "logo": `${SEO_BASE}/logo.png` },
      },
    });
    return () => resetPageSeo();
  }, [lang]);

  return (
    <div className="min-h-[100dvh] bg-black text-white">
      <main className="mx-auto max-w-6xl px-5 py-12">
        <div className="text-center" data-testid="blog-hero">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Bougie<span className="text-sky-400">DuCœur</span>
          </h1>
          <p className="mt-2 text-xs uppercase tracking-[0.25em] text-sky-300/80 sm:text-sm" data-testid="blog-tagline">{t("blog.tagline")}</p>
          <p className="mx-auto mt-4 max-w-xl text-sm text-white/55">{t("blog.intro")}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-sky-400" /></div>
        ) : items.length === 0 ? (
          <p className="py-20 text-center text-white/40" data-testid="blog-empty">{t("blog.empty")}</p>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
            {items.map((p) => (
              <article key={p.id} onClick={() => navigate(`/blog/${p.slug}`)} className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-colors hover:border-sky-400/40 hover:bg-white/[0.06]" data-testid={`blog-card-${p.slug}`}>
                {p.cover_image_url && (
                  <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-black">
                    <img src={`${BACKEND}${p.cover_image_url}`} alt={p.title} className="h-full w-full object-contain" loading="lazy" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="text-lg font-medium leading-snug group-hover:text-sky-300">{p.title}</h2>
                  <p className="mt-2 line-clamp-3 flex-1 text-sm text-white/55">{p.excerpt}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs text-sky-400">{t("blog.read")} <ArrowRight size={12} /></span>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
